import React, { useEffect, useMemo, useState } from 'react';
import { XMarkIcon, ClipboardDocumentIcon, PaperAirplaneIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { quotesService, Quote, QuoteEvent } from '../services/quotesService';
import { useSendQuote } from '../hooks/useQuotesData';

type Props = {
  quote: Quote | null;
  onClose: () => void;
  onQuoteUpdated?: (q: Quote) => void;
};

const formatDateTime = (iso: string) => {
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '' : d.toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });
};

const EVENT_LABELS: Record<string, { label: string; tone: string; icon: string }> = {
  CREATED:            { label: 'Quote created',           tone: 'text-gray-700 bg-gray-100',    icon: '●' },
  UPDATED:            { label: 'Quote edited',            tone: 'text-gray-700 bg-gray-100',    icon: '✎' },
  SENT:               { label: 'Emailed to client',       tone: 'text-blue-700 bg-blue-50',     icon: '✉' },
  EMAIL_DELIVERED:    { label: 'Email delivered',         tone: 'text-blue-700 bg-blue-50',     icon: '✓' },
  EMAIL_OPENED:       { label: 'Client opened email',     tone: 'text-indigo-700 bg-indigo-50', icon: '👁' },
  PORTAL_VIEWED:      { label: 'Client viewed portal',    tone: 'text-indigo-700 bg-indigo-50', icon: '👁' },
  APPROVED:           { label: 'Approved by client',      tone: 'text-emerald-700 bg-emerald-50', icon: '✓' },
  REJECTED:           { label: 'Rejected by client',      tone: 'text-rose-700 bg-rose-50',     icon: '✕' },
  CHANGES_REQUESTED:  { label: 'Changes requested',       tone: 'text-amber-700 bg-amber-50',   icon: '!' },
  AMENDED:            { label: 'Quote amended',           tone: 'text-gray-700 bg-gray-100',    icon: '✎' },
  CONVERTED_TO_JOB:   { label: 'Converted to job',        tone: 'text-emerald-700 bg-emerald-50', icon: '→' },
  EXPIRED:            { label: 'Expired',                 tone: 'text-gray-700 bg-gray-100',    icon: '⏱' },
};

export default function QuoteDetailDrawer({ quote, onClose }: Props) {
  const [events, setEvents] = useState<QuoteEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [copyState, setCopyState] = useState<'idle' | 'copied'>('idle');
  const sendMutation = useSendQuote();

  const portalUrl = useMemo(() => {
    if (!quote?.publicToken) return null;
    return `${window.location.origin}/q/${quote.publicToken}`;
  }, [quote?.publicToken]);

  const [pdfLoading, setPdfLoading] = useState(false);
  const openPdf = async () => {
    if (!quote) return;
    setPdfLoading(true);
    try {
      const blob = await quotesService.getQuotePDF(quote.id);
      const url = URL.createObjectURL(blob);
      const win = window.open(url, '_blank', 'noopener,noreferrer');
      if (!win) {
        // Popup blocked — fall back to download
        const a = document.createElement('a');
        a.href = url;
        a.download = `${quote.quoteNumber}.pdf`;
        a.click();
      }
      // Revoke after a short delay to allow the tab to load
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch (err: any) {
      const status = err?.response?.status;
      const data = err?.response?.data;
      let msg = 'Unable to load PDF';
      if (status === 404) msg = 'PDF not ready yet. Try sending the quote first.';
      else if (data instanceof Blob) {
        try { msg = JSON.parse(await data.text()).message || msg; } catch { /* noop */ }
      } else if (data?.message) msg = data.message;
      alert(msg);
    } finally {
      setPdfLoading(false);
    }
  };

  useEffect(() => {
    if (!quote) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await quotesService.getQuoteEvents(quote.id);
        if (!cancelled) setEvents(res);
      } catch (err) {
        console.error('Failed to load quote events', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [quote?.id]);

  if (!quote) return null;

  const copyPortalLink = async () => {
    if (!portalUrl) return;
    try {
      await navigator.clipboard.writeText(portalUrl);
      setCopyState('copied');
      setTimeout(() => setCopyState('idle'), 1500);
    } catch {
      window.prompt('Copy this link:', portalUrl);
    }
  };

  const handleSend = async () => {
    try {
      await sendMutation.mutateAsync(quote.id);
      // Reload events
      const res = await quotesService.getQuoteEvents(quote.id);
      setEvents(res);
    } catch (err: any) {
      alert(`Failed to send: ${err?.response?.data?.message || err.message}`);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <aside className="w-full max-w-[560px] bg-white h-full overflow-y-auto shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4 sticky top-0 bg-white z-10">
          <div>
            <div className="text-xs text-gray-500 uppercase tracking-wider">Quote</div>
            <div className="text-lg font-semibold text-gray-900">{quote.quoteNumber}</div>
          </div>
          <button onClick={onClose} className="p-2 rounded-md hover:bg-gray-100" aria-label="Close">
            <XMarkIcon className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Summary */}
        <div className="px-5 py-4 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <StatusBadge status={quote.status} />
            <div className="text-right">
              <div className="text-xs text-gray-500">Total</div>
              <div className="text-xl font-bold text-gray-900">£{Number(quote.totalAmount || 0).toFixed(2)}</div>
            </div>
          </div>
          <div className="mt-3 text-sm text-gray-700 font-medium">{quote.jobTitle}</div>
          {quote.description && <p className="mt-1 text-xs text-gray-500 whitespace-pre-wrap">{quote.description}</p>}
          {Array.isArray(quote.requiredSkills) && quote.requiredSkills.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1">
              {quote.requiredSkills.map((s) => (
                <span key={s} className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-indigo-50 text-indigo-700">{s}</span>
              ))}
            </div>
          )}
          {(quote.siteAddress || quote.sitePostcode) && (
            <div className="mt-3 text-xs text-gray-600">
              <div className="font-semibold uppercase text-[10px] tracking-wider text-gray-500 mb-0.5">Site</div>
              {quote.siteAddress} {quote.sitePostcode && <span className="text-gray-500">· {quote.sitePostcode}</span>}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="px-5 py-4 border-b border-gray-100 space-y-3">
          {/* Portal link */}
          {portalUrl ? (
            <div>
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Client portal</div>
              <div className="flex items-center gap-2">
                <input
                  readOnly
                  value={portalUrl}
                  className="flex-1 text-xs px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700"
                />
                <button
                  onClick={copyPortalLink}
                  className="inline-flex items-center gap-1 px-3 py-2 text-xs border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  <ClipboardDocumentIcon className="w-4 h-4" />
                  {copyState === 'copied' ? 'Copied!' : 'Copy'}
                </button>
                <a
                  href={portalUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 px-3 py-2 text-xs border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                  Open
                </a>
              </div>
            </div>
          ) : (
            <div className="text-xs text-gray-500 italic">Portal link will be available once you send the quote.</div>
          )}

          {/* PDF + Send buttons */}
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={openPdf}
              disabled={pdfLoading}
              className="inline-flex items-center gap-1 px-3 py-2 text-xs border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              📄 {pdfLoading ? 'Loading…' : 'Preview PDF'}
            </button>
            {(quote.status === 'DRAFT' || quote.status === 'CHANGES_REQUESTED') && (
              <button
                onClick={handleSend}
                disabled={sendMutation.isPending}
                className="inline-flex items-center gap-1 px-3 py-2 text-xs font-semibold bg-orange-500 text-white rounded-md hover:bg-orange-600 disabled:opacity-50"
              >
                <PaperAirplaneIcon className="w-4 h-4" />
                {sendMutation.isPending ? 'Sending…' : quote.status === 'CHANGES_REQUESTED' ? 'Resend to client' : 'Send to client'}
              </button>
            )}
          </div>
        </div>

        {/* Line items */}
        {Array.isArray(quote.items) && quote.items.length > 0 && (
          <div className="px-5 py-4 border-b border-gray-100">
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Line items</div>
            <div className="space-y-1">
              {quote.items.map((item: any) => (
                <div key={item.id} className="flex justify-between text-xs py-1 border-b border-gray-50 last:border-0">
                  <div className="flex-1 pr-2">
                    <div className="text-gray-900">{item.description}</div>
                    <div className="text-gray-500">{item.quantity} × £{Number(item.unitPrice || 0).toFixed(2)}</div>
                  </div>
                  <div className="font-semibold text-gray-900">£{Number(item.totalPrice || 0).toFixed(2)}</div>
                </div>
              ))}
            </div>
            <div className="mt-3 flex justify-between text-xs">
              <span className="text-gray-500">Subtotal</span>
              <span>£{Number(quote.amount || 0).toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-gray-500">VAT</span>
              <span>£{Number(quote.vatAmount || 0).toFixed(2)}</span>
            </div>
            <div className="mt-1 flex justify-between text-sm font-bold pt-1 border-t">
              <span>Total</span>
              <span>£{Number(quote.totalAmount || 0).toFixed(2)}</span>
            </div>
          </div>
        )}

        {/* Client approval / message */}
        {quote.clientMessage && (
          <div className="px-5 py-4 border-b border-gray-100">
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Client message</div>
            <div className="text-xs text-gray-700 bg-amber-50 border border-amber-100 rounded-md p-3 whitespace-pre-wrap">
              {quote.clientMessage}
              {quote.approvedByClientName && (
                <div className="mt-2 text-[11px] text-gray-500">— {quote.approvedByClientName}</div>
              )}
            </div>
          </div>
        )}

        {/* Timeline */}
        <div className="px-5 py-4 flex-1">
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Activity timeline</div>
          {loading ? (
            <div className="text-xs text-gray-400">Loading…</div>
          ) : events.length === 0 ? (
            <div className="text-xs text-gray-400 italic">No activity yet. Send the quote to start tracking.</div>
          ) : (
            <ol className="relative border-l border-gray-200 ml-2 space-y-3">
              {events.map((ev) => {
                const meta = EVENT_LABELS[ev.eventType] || { label: ev.eventType, tone: 'text-gray-700 bg-gray-100', icon: '●' };
                return (
                  <li key={ev.id} className="ml-3">
                    <span
                      className={`absolute -left-1.5 mt-1 flex items-center justify-center w-3 h-3 rounded-full ring-2 ring-white ${meta.tone}`}
                    />
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-gray-900">{meta.label}</span>
                      <span className="text-[10px] text-gray-400">{formatDateTime(ev.createdAt)}</span>
                    </div>
                    {ev.meta && Object.keys(ev.meta).length > 0 && (
                      <div className="text-[10px] text-gray-500 mt-0.5">
                        {ev.meta.approvedByName ? `By ${ev.meta.approvedByName}` : null}
                        {ev.meta.reason ? `Reason: ${ev.meta.reason}` : null}
                        {ev.meta.message ? `Message: ${ev.meta.message}` : null}
                        {ev.meta.jobId ? `Job created (${String(ev.meta.jobId).slice(0, 8)})` : null}
                      </div>
                    )}
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </aside>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { bg: string; fg: string; label: string }> = {
    DRAFT:              { bg: 'bg-gray-100',    fg: 'text-gray-700',    label: 'Draft' },
    SENT:               { bg: 'bg-blue-50',     fg: 'text-blue-700',    label: 'Sent' },
    ACCEPTED:           { bg: 'bg-emerald-50',  fg: 'text-emerald-700', label: 'Accepted' },
    REJECTED:           { bg: 'bg-rose-50',     fg: 'text-rose-700',    label: 'Rejected' },
    CHANGES_REQUESTED:  { bg: 'bg-amber-50',    fg: 'text-amber-700',   label: 'Changes requested' },
    EXPIRED:            { bg: 'bg-gray-100',    fg: 'text-gray-500',    label: 'Expired' },
  };
  const s = map[status] || map.DRAFT;
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${s.bg} ${s.fg}`}>
      {s.label}
    </span>
  );
}
